import { Injectable } from '@angular/core';
import {
  BOOKING_ERROR,
  IBooking,
  IBookingListParams,
  IBookingReasonPayload,
  ICancelBookingResult,
  ICompleteBookingPayload,
  ICreateBookingPayload,
  ICreateBookingResult,
  IPaginatedResponse,
  IProposeBookingTimePayload,
  ServiceBookingStatus,
  staffBookingActions,
  StaffBookingAction
} from '@pharvio/core/shared';
import { Observable, delay, of, throwError } from 'rxjs';
import {
  BOOKABLE_SERVICE_FIXTURES,
  BOOKING_FIXTURES
} from './fixtures/bookings.fixtures';
import { BookingsDataService } from './repositories/bookings-data.service';

@Injectable()
export class InMemoryBookingsDataService extends BookingsDataService {
  private bookings = [...BOOKING_FIXTURES];

  getBookings(
    params: IBookingListParams = {}
  ): Observable<IPaginatedResponse<IBooking>> {
    const search = params.search?.trim().toLowerCase();
    const rows = this.bookings
      .filter((b) =>
        search
          ? b.bookingNumber.toLowerCase().includes(search) ||
            b.customer.name.toLowerCase().includes(search) ||
            b.serviceName.toLowerCase().includes(search)
          : true
      )
      .filter((b) =>
        params.status?.length ? params.status.includes(b.status) : true
      )
      .filter((b) => (params.branchId ? b.branchId === params.branchId : true))
      .filter((b) =>
        params.customerId ? b.customer.id === params.customerId : true
      )
      .filter((b) =>
        params.bookableServiceId
          ? b.serviceId === params.bookableServiceId
          : true
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

    const page = params.page ?? 1;
    const size = params.size ?? 25;
    const totalPages = Math.max(1, Math.ceil(rows.length / size));
    const data = rows.slice((page - 1) * size, page * size);
    return of({
      data,
      meta: {
        page,
        size,
        totalItems: rows.length,
        totalPages,
        hasPreviousPage: page > 1,
        hasNextPage: page < totalPages,
        totalPageItems: data.length
      }
    }).pipe(delay(150));
  }

  getBooking(id: string): Observable<IBooking> {
    const row = this.bookings.find((b) => b.id === id);
    return row ? of(row).pipe(delay(150)) : this.notFound();
  }

  createBooking(
    payload: ICreateBookingPayload
  ): Observable<ICreateBookingResult> {
    const service = BOOKABLE_SERVICE_FIXTURES.find(
      (s) => s.id === payload.bookableServiceId
    );
    if (!service) {
      return throwError(() => ({
        statusCode: 404,
        code: BOOKING_ERROR.SERVICE_NOT_FOUND,
        message: 'That service no longer exists.'
      }));
    }

    const total =
      service.priceMinor +
      Math.round((service.priceMinor * service.taxClass.rateBps) / 10000);
    const deposit = Math.round((total * service.depositPercent) / 100);
    const id = crypto.randomUUID();

    const booking: IBooking = {
      id,
      bookingNumber: `BKG-202610-${id.slice(0, 6).toUpperCase()}`,
      // No deposit due means the booking skips straight to `requested`.
      status: deposit > 0 ? 'pending_deposit' : 'requested',
      source: 'staff',
      branchId: payload.branchId,
      branchName: 'Ikeja',
      serviceId: service.id,
      serviceName: service.name,
      durationMinutes: service.durationMinutes,
      requestedAt: payload.requestedAt,
      proposedAt: null,
      scheduledAt: null,
      depositPercent: service.depositPercent,
      depositAmountMinor: deposit,
      balanceAmountMinor: total - deposit,
      totalMinor: total,
      currency: 'NGN',
      depositPaidAt: null,
      balancePaidAt: null,
      depositExpiresAt:
        deposit > 0
          ? new Date(Date.now() + 24 * 3_600_000).toISOString()
          : null,
      depositOrder:
        deposit > 0
          ? {
              id: `${id}-dep`,
              orderNumber: `ORD-${id.slice(0, 6).toUpperCase()}`,
              leg: 'deposit',
              status: 'pending_payment',
              source: 'staff',
              totalMinor: deposit,
              serviceFeeTotalMinor: 0,
              currency: 'NGN',
              reference: null,
              paymentLinkToken: null,
              paymentLinkExpiresAt: null,
              paymentReceivedAt: null,
              invoiceId: null,
              invoiceNumber: null
            }
          : null,
      balanceOrder: null,
      customerNotes: payload.customerNotes ?? null,
      declineReason: null,
      cancellationReason: null,
      confirmedAt: null,
      completedAt: null,
      cancelledAt: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      customer: {
        id: payload.customerId,
        name: 'Walk-in customer',
        email: null,
        phone: null
      },
      staffNotes: payload.staffNotes ?? null,
      servicePriceMinor: service.priceMinor,
      startedAt: null,
      deliveredAt: null,
      noShowAt: null,
      expiredAt: null,
      cancelledBy: null,
      createdBy: { kind: 'user', name: 'You' }
    };

    this.bookings = [booking, ...this.bookings];
    // Staff legs never auto-initialise Paystack.
    return of({ booking, paymentLinkUrl: null, reference: null }).pipe(
      delay(150)
    );
  }

  confirm(id: string): Observable<IBooking> {
    return this.transition(id, 'confirm', (b) => ({
      ...b,
      status: 'confirmed',
      scheduledAt: b.proposedAt ?? b.requestedAt,
      confirmedAt: new Date().toISOString()
    }));
  }

  proposeTime(
    id: string,
    payload: IProposeBookingTimePayload
  ): Observable<IBooking> {
    return this.transition(id, 'propose-time', (b) => ({
      ...b,
      status: 'time_proposed',
      proposedAt: payload.proposedAt,
      staffNotes: payload.staffNotes ?? b.staffNotes
    }));
  }

  start(id: string): Observable<IBooking> {
    return this.transition(id, 'start', (b) => ({
      ...b,
      status: 'in_progress',
      startedAt: new Date().toISOString()
    }));
  }

  complete(
    id: string,
    payload: ICompleteBookingPayload
  ): Observable<ICreateBookingResult> {
    const current = this.bookings.find((b) => b.id === id);
    if (!current) return this.notFound();
    if (!staffBookingActions(current.status).includes('complete')) {
      return this.invalidTransition();
    }

    const dueMinor = current.balanceAmountMinor;
    const now = new Date().toISOString();
    const updated: IBooking = {
      ...current,
      // The branch the caller has to read back rather than assume.
      status: dueMinor > 0 ? 'pending_balance' : 'completed',
      deliveredAt: now,
      completedAt: dueMinor > 0 ? null : now,
      staffNotes: payload.staffNotes ?? current.staffNotes,
      balanceOrder:
        dueMinor > 0
          ? {
              id: `${id}-bal`,
              orderNumber: `ORD-${id.slice(0, 6).toUpperCase()}-B`,
              leg: 'balance',
              status: 'pending_payment',
              source: 'staff',
              totalMinor: dueMinor,
              serviceFeeTotalMinor: 0,
              currency: current.currency,
              reference: null,
              paymentLinkToken: null,
              paymentLinkExpiresAt: null,
              paymentReceivedAt: null,
              invoiceId: null,
              invoiceNumber: null
            }
          : current.balanceOrder,
      updatedAt: now
    };
    this.bookings = this.bookings.map((b) => (b.id === id ? updated : b));
    return of({ booking: updated, paymentLinkUrl: null, reference: null }).pipe(
      delay(150)
    );
  }

  decline(
    id: string,
    payload: IBookingReasonPayload
  ): Observable<ICancelBookingResult> {
    return this.closeOut(id, 'decline', 'declined', {
      declineReason: payload.reason
    });
  }

  noShow(id: string): Observable<ICancelBookingResult> {
    // A no-show keeps the deposit, so there is nothing to refund.
    return this.closeOut(id, 'no-show', 'no_show', {
      noShowAt: new Date().toISOString()
    });
  }

  cancel(
    id: string,
    payload: IBookingReasonPayload
  ): Observable<ICancelBookingResult> {
    return this.closeOut(id, 'cancel', 'cancelled', {
      cancellationReason: payload.reason,
      cancelledAt: new Date().toISOString()
    });
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private transition(
    id: string,
    action: StaffBookingAction,
    patch: (b: IBooking) => IBooking
  ): Observable<IBooking> {
    const current = this.bookings.find((b) => b.id === id);
    if (!current) return this.notFound();
    if (!staffBookingActions(current.status).includes(action)) {
      return this.invalidTransition();
    }
    const updated = { ...patch(current), updatedAt: new Date().toISOString() };
    this.bookings = this.bookings.map((b) => (b.id === id ? updated : b));
    return of(updated).pipe(delay(150));
  }

  private closeOut(
    id: string,
    action: StaffBookingAction,
    status: ServiceBookingStatus,
    extra: Partial<IBooking>
  ): Observable<ICancelBookingResult> {
    const current = this.bookings.find((b) => b.id === id);
    if (!current) return this.notFound();
    if (!staffBookingActions(current.status).includes(action)) {
      return this.invalidTransition();
    }
    const updated: IBooking = {
      ...current,
      ...extra,
      status,
      updatedAt: new Date().toISOString()
    };
    this.bookings = this.bookings.map((b) => (b.id === id ? updated : b));
    return of({
      ...updated,
      // Non-null means a deposit was taken and nothing has been refunded.
      depositInvoiceId:
        status === 'no_show' ? null : (current.depositOrder?.invoiceId ?? null)
    }).pipe(delay(150));
  }

  private notFound<T>(): Observable<T> {
    return throwError(() => ({
      statusCode: 404,
      code: BOOKING_ERROR.BOOKING_NOT_FOUND,
      message: 'That booking no longer exists.'
    }));
  }

  private invalidTransition<T>(): Observable<T> {
    return throwError(() => ({
      statusCode: 409,
      code: BOOKING_ERROR.INVALID_TRANSITION,
      message: 'This booking has moved on. Refresh and try again.'
    }));
  }
}
